<template>
  <Card>
    <h1 class="text-h2 text-center mb-16">Let's find the sitters closest to you</h1>
    <p class="text-body text-center mb-24">We have sitters in 96% of U.S. neighborhoods.</p>
    <form>
      <div class="mb-32">
        <TextField
          id="zipCode"
          v-model="zipCode"
          labelName="ZIP code"
          name="postal-code"
          type="text"
          maxlength="5"
          pattern="[0-9]*"
          inputmode="numeric"
          :aria-label="errors.zipCode || 'Five-digit zip code'"
          :status="helperStatus"
          :helperText="errors.zipCode || 'Please enter a five-digit ZIP code.'"
        />
      </div>
      <div class="flex flex-row-reverse">
        <RouteStepperNextButton :draftData="draft" :validate="validateFunc" @errors="errors = $event" />
      </div>
    </form>
  </Card>
</template>

<script lang="ts">
import { HelperStatus } from '@/helpers/status';
import { isValidZipcode } from '@/helpers/addressHelper';
import { zipToLocationDetails } from '@/helpers/geocoding';
import { isOn } from '@/lib/growthbook';
import { geocode, type Coordinates } from '@/lib/geocode';
import TextField from '@/components/textfield/TextField.vue';
import Card from '@/components/card/Card.vue';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';
import { OnboardingSeekerDraft } from '../types';

interface Errors {
  zipCode?: string;
}

interface OnboardingSeekerLocationData extends OnboardingSeekerDraft {
  errors: Errors;
}

export default {
  name: 'OnboardingSeekerLocation',
  components: {
    Card,
    TextField,
    RouteStepperNextButton,
  },
  data(): OnboardingSeekerLocationData {
    return {
      zipCode: null,
      location: null,
      errors: {},
    };
  },
  computed: {
    draft(): OnboardingSeekerDraft {
      return {
        zipCode: this.zipCode,
        location: this.location,
      };
    },
    helperStatus(): HelperStatus {
      if (this.errors.zipCode) {
        return HelperStatus.Error;
      }
      return HelperStatus.Neutral;
    },
  },
  watch: {
    zipCode(): void {
      this.resetValidation();
      this.location = null;
    },
  },
  methods: {
    resetValidation(): void {
      this.errors = {};
    },
    async geocodeLocation(): Promise<Coordinates | null> {
      try {
        if (isOn('new-geocode-query')) {
          // eslint-disable-next-line no-console
          console.log('new-geocode-query is on');
          return await geocode({ zipCode: this.zipCode });
        }
        const details = await zipToLocationDetails(this.zipCode as string);
        if (!details) return null;
        return { x: details.x, y: details.y };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error verifying location:', error);
        return null;
      }
    },
    async validateFunc(): Promise<Errors> {
      const errors: Errors = {};
      if (!isValidZipcode(this.zipCode)) {
        errors.zipCode = 'ZIP code is required. Enter a five digit ZIP code.';
        return errors;
      }
      this.location = await this.geocodeLocation();

      if (!this.location) {
        errors.zipCode = 'Unable to find location. Please check the ZIP code.';
      }
      return errors;
    },
  },
};
</script>
